import clsx from 'clsx'
import PropTypes from 'prop-types'
import Typo from 'constants/typography'
import Button from 'components/common/button'
import Icon, { ICONS, ICON_SIZE } from 'components/common/icon'
import { BUTTON_TYPES } from 'constants/button'
import { withI18next } from 'lib/i18n/withI18next'
import { getCartProductUrl } from 'components/cart/utils'
import styles from './styles.scss'
import { getClassNameWithOpacity } from '../utils'

const CartProductName = ({
  deleteFromCart,
  product,
  isSoldOut,
  isFreeGift,
  openFreeGift,
  onLinkClicked,
  isDiamondProduct,
  isShoppingBagNewDesign,
  fromSummary,
  t,
}) => {
  const productNameClass = getClassNameWithOpacity(
    'cart-item-info__product-name',
    isSoldOut,
    isDiamondProduct,
  )

  // Delete sits beside the name, not on the product image. Checked at both
  // call sites below via !fromSummary -- this same component renders the
  // read-only order-review line on checkout (fromSummary, see
  // CartItemList/CartItems), where there's nothing to remove;
  // isShoppingBagNewDesign alone isn't enough to tell that apart from the
  // editable cart drawer, since it's hardcoded true for both
  // (CartItems.js).
  const renderActions = () => (
    <div className="cart-product-actions">
      <Button
        type={BUTTON_TYPES.link}
        className="cart-product-actions__delete"
        onClick={deleteFromCart}
      >
        <Icon
          alt="cart-remove"
          src={ICONS.delete}
          size={ICON_SIZE.smallMedium}
        />
      </Button>
    </div>
  )

  if (isSoldOut) {
    return (
      <div className={productNameClass}>
        <style jsx>{styles}</style>
        <div className="cart-item-info__product-name-row">
          <span className={clsx(Typo.body2, 'product-name')}>
            {product.name}
            <span className={clsx(Typo.caption, 'sold-out-label')}>
              {t('Sold Out')}
            </span>
          </span>
          {/* Unlike the normal line below, a sold-out one stays removable on
            the checkout order review (fromSummary): Place Order is disabled
            and relabelled "Remove Sold Out Item" while one is in the bag
            (checkout-redesign/index.js), and checkout has no other way
            back to the bag to act on that -- without this the shopper was
            told to remove something they had no control to remove. */}
          {isShoppingBagNewDesign && renderActions()}
        </div>
      </div>
    )
  }

  const renderFreeGift = () => {
    if (isShoppingBagNewDesign) {
      return (
        <div className="free-gift-info">
          <div className="free-gift-button">
            <button onClick={openFreeGift} className="product-name">
              <span className={Typo.subtitle2}>{t('Free gift added')}</span>
            </button>
          </div>

          <span className={Typo.body2}>{product.name}</span>
        </div>
      )
    }

    return (
      <button onClick={openFreeGift} className="product-name">
        <span className={Typo.subtitle2}>{t('Free gift added')}</span>
        <span className={Typo.caption}>{product.name}</span>
      </button>
    )
  }

  // The checkout order-review line is read-only -- nothing here opens the
  // PDP, so the name is plain text there instead of a link.
  const renderName = () =>
    fromSummary ? (
      <span className={clsx(Typo.body2, 'product-name')}>{product.name}</span>
    ) : (
      <a
        href={getCartProductUrl(product)}
        onClick={onLinkClicked}
        className={clsx(Typo.body2, 'product-name')}
      >
        {product.name}
      </a>
    )

  return (
    <div
      className={clsx(productNameClass, {
        'new-design': isShoppingBagNewDesign,
      })}
    >
      <style jsx>{styles}</style>
      <div className="cart-item-info__product-name-row">
        {isFreeGift ? renderFreeGift() : renderName()}
        {isShoppingBagNewDesign && !fromSummary && renderActions()}
      </div>
    </div>
  )
}

CartProductName.defaultProps = {
  isShoppingBagNewDesign: false,
  fromSummary: false,
}

CartProductName.propTypes = {
  t: PropTypes.func.isRequired,
  deleteFromCart: PropTypes.func.isRequired,
  product: PropTypes.shape({
    name: PropTypes.string,
  }).isRequired,
  isSoldOut: PropTypes.bool.isRequired,
  isFreeGift: PropTypes.bool.isRequired,
  isShoppingBagNewDesign: PropTypes.bool,
  fromSummary: PropTypes.bool,
  openFreeGift: PropTypes.func.isRequired,
  onLinkClicked: PropTypes.func.isRequired,
  isDiamondProduct: PropTypes.bool.isRequired,
}

export default withI18next()(CartProductName)
